import { useState, useEffect, useRef } from 'react';
import type { ThesconAiScopeConfig } from '../components/ThesconAiScopePanel';
import { useProjectDocuments } from './useProjectDocuments';
import { useKnowledgeDocuments } from './useKnowledgeDocuments';

export type ThesconAiThinkingActivityLine = { id: string; text: string };

const FIRST_LINE_MS = 450;
const BETWEEN_LINES_MS = 3000;
const MAX_READING = 6;
const MAX_NAME_LEN = 52;

function getDocId(d: { id: string; documentId?: string }) {
  return d.documentId || d.id;
}

function ellipsizeFilename(name: string): string {
  const t = name.trim() || '…';
  if (t.length <= MAX_NAME_LEN) return t;
  return `${t.slice(0, MAX_NAME_LEN - 1)}…`;
}

function resolveSelectedFilenames(
  cfg: ThesconAiScopeConfig,
  projectDocs: { id: string; documentId?: string; filename: string }[],
  companyDocs: { id: string; documentId?: string; filename: string }[],
  knowledgeDocs: { id: string; documentId?: string; filename: string }[]
): string[] {
  const names: string[] = [];
  const addFrom = (
    ids: string[],
    docs: { id: string; documentId?: string; filename: string }[]
  ) => {
    const map = new Map(docs.map((d) => [getDocId(d), d.filename] as const));
    for (const id of ids) {
      const fn = map.get(id);
      names.push(ellipsizeFilename(fn ?? id));
    }
  };
  if (cfg.projectId) addFrom(cfg.selectedProjectDocumentIds, projectDocs);
  if (cfg.companyId) addFrom(cfg.selectedCompanyDocumentIds, companyDocs);
  const kColl =
    cfg.knowledgeId && cfg.knowledgeId !== cfg.companyId ? cfg.knowledgeId : undefined;
  if (kColl) addFrom(cfg.selectedKnowledgeDocumentIds, knowledgeDocs);
  return names;
}

function translateOr(
  t: (key: string) => string,
  key: string,
  fallback: string
): string {
  const v = t(key);
  return v === key ? fallback : v;
}

function readingLine(t: (key: string) => string, name: string): string {
  const key = 'thescon_ai_thinking_reading';
  const v = t(key);
  if (v === key) return `Reading "${name}"…`;
  return v.replace('{name}', name);
}

/**
 * Staged “activity” lines under Thinking on Thescon AI — purely cosmetic, driven by scope + loaded document metadata.
 */
export function useThesconAiThinkingActivity(
  scopeConfig: ThesconAiScopeConfig,
  isThinking: boolean,
  t: (key: string) => string
): ThesconAiThinkingActivityLine[] {
  const { documents: projectDocuments } = useProjectDocuments(
    scopeConfig.projectId || undefined
  );
  const { documents: companyDocuments } = useKnowledgeDocuments(
    scopeConfig.companyId || undefined
  );
  const knowledgeCollectionId =
    scopeConfig.knowledgeId && scopeConfig.knowledgeId !== scopeConfig.companyId
      ? scopeConfig.knowledgeId
      : undefined;
  const { documents: knowledgeDocuments } = useKnowledgeDocuments(knowledgeCollectionId);

  const [lines, setLines] = useState<ThesconAiThinkingActivityLine[]>([]);

  const scopeRef = useRef(scopeConfig);
  const projectRef = useRef(projectDocuments);
  const companyRef = useRef(companyDocuments);
  const knowledgeRef = useRef(knowledgeDocuments);
  scopeRef.current = scopeConfig;
  projectRef.current = projectDocuments;
  companyRef.current = companyDocuments;
  knowledgeRef.current = knowledgeDocuments;

  useEffect(() => {
    if (!isThinking) {
      setLines([]);
      return;
    }

    const cfg = scopeRef.current;
    const names = resolveSelectedFilenames(
      cfg,
      projectRef.current,
      companyRef.current,
      knowledgeRef.current
    );
    const rag = !!(cfg.projectId || cfg.companyId || cfg.knowledgeId);

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const push = (text: string) => {
      setLines((prev) => [...prev, { id: crypto.randomUUID(), text }]);
    };

    const sched = (fn: () => void, ms: number) => {
      timeouts.push(setTimeout(fn, ms));
    };

    if (rag && names.length > 0) {
      const shown = names.slice(0, MAX_READING);
      const overflow = names.length - shown.length;
      shown.forEach((name, i) => {
        sched(() => push(readingLine(t, name)), FIRST_LINE_MS + i * BETWEEN_LINES_MS);
      });
      let nextDelay = FIRST_LINE_MS + shown.length * BETWEEN_LINES_MS;
      if (overflow > 0) {
        const moreKey = 'thescon_ai_thinking_more';
        const moreTpl = t(moreKey);
        const moreText =
          moreTpl === moreKey
            ? `… and ${overflow} more document${overflow === 1 ? '' : 's'}`
            : moreTpl.replace('{count}', String(overflow));
        sched(() => push(moreText), nextDelay);
        nextDelay += BETWEEN_LINES_MS;
      }
      sched(
        () =>
          push(
            translateOr(
              t,
              'thescon_ai_thinking_synthesizing',
              'Putting it together…'
            )
          ),
        nextDelay
      );
    } else if (rag) {
      sched(
        () =>
          push(
            translateOr(t, 'thescon_ai_thinking_scope', 'Searching your scope…')
          ),
        FIRST_LINE_MS
      );
      sched(
        () =>
          push(
            translateOr(
              t,
              'thescon_ai_thinking_synthesizing',
              'Putting it together…'
            )
          ),
        FIRST_LINE_MS + BETWEEN_LINES_MS
      );
    } else {
      sched(
        () =>
          push(
            translateOr(t, 'thescon_ai_thinking_general', 'Drafting a reply…')
          ),
        FIRST_LINE_MS
      );
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only (re)schedule when a thinking session starts/ends
  }, [isThinking, t]);

  return lines;
}
